/**
 * N1 · Details step 4 of 4 — DELIVERY, and the address stays ONE screen.
 *
 * Address is one thought, not six screens, so the shared AddressFields group is
 * rendered whole. There is still exactly one address form in this codebase.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { readDetails, readSelectedVariant, recordFunnelEvent, saveDetails } from "@/lib/consumer-session";
import AddressFields from "@/components/consumer/AddressFields";
import DetailsStepPage from "@/components/consumer/DetailsStepPage";
import { Field, Pill, inputClass } from "@/components/consumer/chrome";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S6_SECTION, S6_DEFAULTS } from "@/content/screens/s6";
import {
  addressDraftFrom,
  canonicalAddress,
  type AddressDraft,
} from "@/lib/postal-address";
import {
  addressStepMissing,
  firstIncompleteStep,
  nextDetailsStep,
  prevDetailsStep,
} from "@/lib/details-steps";
import { resolveDeliveryMethods, resolvePanDigits, type DeliveryMethod } from "@/lib/consumer-config";
import { useConsumerFlow } from "../../ConsumerFlow";
import { useFieldCompletion } from "@/hooks/useFieldCompletion";
import { useKnownDetailsPrefill } from "@/lib/details-prefill";
import { addressDraftFrom as draftFrom } from "@/lib/postal-address";

export default function AddressStep() {
  const { program } = useConsumerFlow();
  const navigate = useNavigate();
  const { t } = useSiteContent(S6_SECTION, S6_DEFAULTS);
  const cfg = (program.config ?? {}) as Record<string, any>;

  const deliveryOptions: DeliveryMethod[] = useMemo(() => resolveDeliveryMethods(cfg), [cfg]);
  const panDigits = useMemo(() => resolvePanDigits(cfg), [cfg]);
  const existing = readDetails(program.slug);

  const [deliveryType, setDeliveryType] = useState<DeliveryMethod>(
    (existing?.delivery_type as any) ?? deliveryOptions[0],
  );
  const [branchId, setBranchId] = useState(existing?.branch_id ?? "");
  const [pan, setPan] = useState(existing?.pan_last5 ?? "");
  const [address, setAddress] = useState<AddressDraft>(() => ({
    ...addressDraftFrom(existing?.postal_address as Record<string, string> | null),
    country: "US",
  }));

  useEffect(() => {
    if (!readSelectedVariant(program.slug)) {
      navigate("../reveal", { replace: true });
      return;
    }
    const step = firstIncompleteStep(readDetails(program.slug), 0);
    if (step === "name" || step === "phone" || step === "dob") {
      navigate(`../${step}`, { replace: true });
    }
  }, [program.slug, navigate]);

  const missing = addressStepMissing({ deliveryType, branchId, address, pan, panDigits });

  /* ONE screen, SEVERAL fields — so each required field is recorded separately.
     A drop on this screen otherwise says "the address screen" and nothing about
     which line people stall on. Postal only: an in-branch collection has no
     address to complete. */
  const posting = deliveryType === "mail";
  useFieldCompletion("field_address_line1", posting && Boolean(address.line1?.trim()));
  useFieldCompletion("field_address_city", posting && Boolean(address.city?.trim()));
  useFieldCompletion("field_address_region", posting && Boolean(address.region?.trim()));
  useFieldCompletion("field_address_postcode", posting && Boolean(address.postcode?.trim()));
  useFieldCompletion("field_address_complete", !missing);

  /* PREFILL: the stored delivery address, only into fields still empty. The
     value written on continue goes to the NEW order draft alone — no profile
     write, and no touch of any confirmed order's address snapshot. */
  useKnownDetailsPrefill(program.slug, (known) => {
    if (known.postal_address) {
      setAddress((cur) =>
        cur.line1?.trim() || cur.city?.trim() || cur.postcode?.trim()
          ? cur
          : { ...draftFrom(known.postal_address as Record<string, string>), country: "US" },
      );
    }
  });

  function onContinue() {
    /* THE DETAILS STAGE EVENT. It used to fire from the fee-only screen; that
       screen is gone, so the last details step emits it and the funnel table
       still shows the stage. */
    recordFunnelEvent(program.slug, "details", program.program_id);
    saveDetails(program.slug, {
      ...(readDetails(program.slug) ?? {}),
      delivery_type: deliveryType,
      branch_id: deliveryType === "in_branch" ? (branchId.trim() || null) : null,
      postal_address: deliveryType === "mail" ? canonicalAddress({ ...address, country: "US" }) : null,
      pan_last5: panDigits > 0 ? pan.trim() : null,
    } as any);
    navigate(nextDetailsStep("address"));
  }

  return (
    <DetailsStepPage
      testId="s6-address-step"
      backTo={prevDetailsStep("address")}
      backLabel={t("s6.header.backLabel")}
      step="4 of 4"
      title={t("s6.address.title")}
      missing={missing}
      continueLabel={t("s6.footer.continue")}
      onContinue={onContinue}
    >
      {deliveryOptions.length > 1 && (
        <fieldset className="mb-5" data-testid="s6-delivery">
          <legend className="mb-2 text-[14px] font-semibold">{t("s6.delivery.title")}</legend>
          <div className="flex flex-wrap gap-2">
            {deliveryOptions.map((opt) => (
              <Pill
                key={opt}
                type="button"
                active={deliveryType === opt}
                onClick={() => setDeliveryType(opt)}
                data-testid={`s6-delivery-${opt}`}
              >
                {opt === "in_branch" ? t("s6.delivery.inBranch") : t("s6.delivery.mail")}
              </Pill>
            ))}
          </div>
        </fieldset>
      )}

      {deliveryType === "in_branch" && (
        <Field label={t("s6.delivery.branchLabel")}>
          <input
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            placeholder={t("s6.delivery.branchPlaceholder")}
            className={inputClass}
            data-testid="s6-branch"
          />
        </Field>
      )}

      {deliveryType === "mail" && (
        <AddressFields
          showCountry={false}
          value={address}
          onChange={(patch) => setAddress((a) => ({ ...a, ...patch }))}
          testId="s6-postal"
          showTitle={false}
          labels={{
            zipLabel: t("s6.address.zipLabel"),
            zipPlaceholder: t("s6.address.zipPlaceholder"),
            zipError: t("s6.address.zipError"),
            change: t("s6.address.change"),
            street: t("s6.address.street"),
            streetPlaceholder: t("s6.address.streetPlaceholder"),
            unit: t("s6.address.unit"),
            optionalBadge: t("s6.name.optionalBadge"),
            unitPlaceholder: t("s6.address.unitPlaceholder"),
            city: t("s6.address.city"),
            state: t("s6.address.state"),
            usOnly: t("s6.address.usOnly"),
          }}
        />
      )}

      {panDigits > 0 && (
        <Field
          label={t("s6.pan.label")}
          hint={t("s6.pan.hint").replace("{digits}", String(panDigits))}
          className="mt-5"
        >
          <input
            value={pan}
            onChange={(e) => setPan(e.target.value.replace(/\D/g, "").slice(0, panDigits))}
            inputMode="numeric"
            maxLength={panDigits}
            className={inputClass}
            data-testid="s6-pan"
          />
        </Field>
      )}
    </DetailsStepPage>
  );
}
